import { useState } from 'react';
import { useAppState } from '../../app/state';
import { canAiAdmin, canViewLogs } from '../../lib/permissions';
import { DocHistoryView } from './DocHistoryView';
import { ActionsView } from './ActionsView';

/**
 * THE LOGS TAB (V0.49) — two views of two different things, behind two different roles.
 *
 * ACTIONS is the action log: every act by every person and by the AI, across the whole
 * installation, behind the standalone `SHOW_LOGS`. DOCUMENT HISTORY is the V0.20 document log,
 * unchanged and still behind `AI_ADMIN`, because the prompts, coverage reports and token counts
 * it shows are operator tooling rather than a record of who did what.
 *
 * THE TWO GATES ARE INDEPENDENT, which is the whole reason this is a strip rather than one
 * table. An AI operator with no SHOW_LOGS sees only the history — exactly what they saw before
 * this release, reached by exactly the same `openLogs()`. An administrator with only SHOW_LOGS
 * sees only the actions. Somebody with both gets the strip. Nobody is shown a tab that would
 * answer 403, and `Dashboard.tsx` does not draw this tab at all without one of the two.
 *
 * `initialView` is what `openLogs()` passes through: four buttons and `e2e/25-logs.spec.ts`
 * call it meaning the document history, and it still lands there. A plain click on the tab
 * opens whichever view the reader is entitled to, Actions first.
 */
export function LogsTab({
  initialView,
  onLeave,
}: {
  initialView?: 'actions' | 'history';
  onLeave: () => void;
}) {
  const s = useAppState();
  const mayActions = canViewLogs(s.perm);
  const mayHistory = canAiAdmin(s.perm);

  const [view, setView] = useState<'actions' | 'history'>(() => {
    if (initialView === 'history' && mayHistory) return 'history';
    if (initialView === 'actions' && mayActions) return 'actions';
    return mayActions ? 'actions' : 'history';
  });

  // A reader entitled to only one of the two is never shown a strip with one button on it.
  const both = mayActions && mayHistory;
  const showing = view === 'actions' && !mayActions ? 'history' : view === 'history' && !mayHistory ? 'actions' : view;

  return (
    <div id="dash-logs-tab">
      {both ? (
        <div className="perm-tabs sub" id="logs-views" role="tablist" aria-label="Logs views">
          <button
            className={showing === 'actions' ? 'perm-tab on' : 'perm-tab'}
            id="logs-view-actions"
            role="tab"
            aria-selected={showing === 'actions'}
            onClick={() => setView('actions')}
          >
            Actions
          </button>
          <button
            className={showing === 'history' ? 'perm-tab on' : 'perm-tab'}
            id="logs-view-history"
            role="tab"
            aria-selected={showing === 'history'}
            onClick={() => setView('history')}
          >
            Document history
          </button>
        </div>
      ) : null}

      {showing === 'history' ? (
        <DocHistoryView onLeave={onLeave} />
      ) : (
        <ActionsView onLeave={onLeave} />
      )}
    </div>
  );
}
